import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Lock } from "lucide-react";
import { BELTS } from "../../data/belts";
import { beltForWeek } from "../../lib/journey";

const SENSEI_IMG =
  "https://customer-assets.emergentagent.com/job_iceland-karate-quest/artifacts/q4dtx84g_image.png";

const SENSEI_PHRASES = ["Þú stendur þig vel!", "Ekki gefast upp!"];

/**
 * Iceland silhouette with recognisable features:
 *  - Reykjanes peninsula (small triangular tab) at SW
 *  - Snæfellsnes peninsula (long thin tongue) sticking west
 *  - Westfjords (large notched bulge) sticking up NW
 *  - North coast running east with subtle peninsulas
 *  - East fjords cluster
 *  - Smoother south coast
 * viewBox 0 0 1000 600.
 */
const ICELAND_PATH = `
  M 148 405
  L 110 410
  L 78 392
  L 88 368
  L 130 358
  L 175 348
  L 200 332
  L 188 310
  L 145 305
  L 95 302
  L 58 295
  L 50 275
  L 70 262
  L 115 258
  L 165 264
  L 210 272
  L 226 254
  L 234 226
  L 222 198
  L 208 172
  L 200 148
  L 215 124
  L 245 116
  L 268 128
  L 280 110
  L 270 86
  L 295 70
  L 332 68
  L 368 78
  L 402 72
  L 432 88
  L 446 112
  L 438 138
  L 415 152
  L 388 162
  L 372 178
  L 395 192
  L 432 200
  L 478 202
  L 528 198
  L 580 200
  L 632 204
  L 685 210
  L 735 222
  L 780 240
  L 820 262
  L 850 286
  L 872 314
  L 882 348
  L 868 378
  L 882 402
  L 858 425
  L 818 438
  L 775 438
  L 732 442
  L 688 452
  L 642 462
  L 595 465
  L 545 468
  L 495 470
  L 445 465
  L 395 458
  L 348 450
  L 302 442
  L 258 432
  L 215 418
  L 180 408
  Z
`.replace(/\s+/g, " ");

const SENSEI_LEAD_DAYS = 3;
// Legacy on-path minimum kept for reference — Sandra now sits AT the next belt
// rather than being interpolated along the path, so these are no longer used
// to compute her position (kept only for the path-length effect timing).
const MIN_SENSEI_GAP_PX = 55;

export const IcelandMap = ({
  activeId,
  onSelect,
  progress = 0,
  currentWeek = 1,
  totalDays = 189,
}) => {
  // A hidden, non-animated path that we use purely to compute getPointAtLength.
  // (Animating the visible path can interfere with ref/getTotalLength timing.)
  const measurePathRef = useRef(null);

  const [userPoint, setUserPoint] = useState(null);
  const [pathLength, setPathLength] = useState(0);
  const [completedLength, setCompletedLength] = useState(0);
  const [senseiMsg, setSenseiMsg] = useState(null);

  // Sandra always waits at the belt AFTER the learner's current one.
  const currentBelt = beltForWeek(BELTS, currentWeek);
  const currentIdx = BELTS.findIndex((b) => b.id === currentBelt.id);
  const nextBelt = BELTS[currentIdx + 1] || currentBelt;
  const senseiBelt = nextBelt.locked
    ? currentBelt // if next is Black (locked), keep Sandra one step behind on Purple
    : nextBelt;
  const senseiPoint = { x: senseiBelt.map.x + 48, y: senseiBelt.map.y };

  const handleSenseiClick = (e) => {
    e.stopPropagation();
    const next = SENSEI_PHRASES[Math.floor(Math.random() * SENSEI_PHRASES.length)];
    setSenseiMsg({ text: next, key: Date.now() });
  };

  // Auto-dismiss the sensei speech bubble after ~3.2s.
  useEffect(() => {
    if (!senseiMsg) return undefined;
    const t = setTimeout(() => setSenseiMsg(null), 3200);
    return () => clearTimeout(t);
  }, [senseiMsg]);

  const route = BELTS.filter((b) => !b.locked);
  const routePath = route.reduce((acc, p, i) => {
    if (i === 0) return `M ${p.map.x} ${p.map.y}`;
    const prev = route[i - 1];
    const cx = (prev.map.x + p.map.x) / 2;
    const cy = Math.min(prev.map.y, p.map.y) - 30;
    return `${acc} Q ${cx} ${cy} ${p.map.x} ${p.map.y}`;
  }, "");

  useEffect(() => {
    if (!measurePathRef.current) return;
    let mounted = true;
    const compute = () => {
      try {
        const total = measurePathRef.current.getTotalLength();
        if (!total) return;
        const clampedProgress = Math.max(0, Math.min(1, progress));
        const userLen = total * clampedProgress;
        const userPt = measurePathRef.current.getPointAtLength(userLen);
        if (mounted) {
          setUserPoint({ x: userPt.x, y: userPt.y });
          setPathLength(total);
          setCompletedLength(userLen);
        }
      } catch {
        // ignore
      }
    };
    const raf = requestAnimationFrame(compute);
    return () => {
      mounted = false;
      cancelAnimationFrame(raf);
    };
  }, [progress, totalDays]);

  return (
    <div
      data-testid="iceland-map"
      className="relative w-full rounded-[28px] border border-zinc-200/80 bg-gradient-to-br from-[#F5FBF7] via-white to-[#F0F9F3] p-2 sm:p-3 shadow-[0_30px_60px_-30px_rgba(33,183,102,0.25)] overflow-hidden"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.05]"
        style={{
          backgroundImage: "radial-gradient(#121212 1px, transparent 1px)",
          backgroundSize: "16px 16px",
        }}
      />

      <svg
        viewBox="0 0 1000 600"
        className="relative w-full h-auto"
        role="img"
        aria-label="Map of Iceland with 27-week training path"
      >
        <defs>
          <filter id="softShadow" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur in="SourceAlpha" stdDeviation="6" />
            <feOffset dx="0" dy="6" result="off" />
            <feComponentTransfer>
              <feFuncA type="linear" slope="0.18" />
            </feComponentTransfer>
            <feMerge>
              <feMergeNode />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <clipPath id="senseiClip">
            <circle cx="0" cy="0" r="17" />
          </clipPath>
        </defs>

        {/* Iceland silhouette */}
        <motion.path
          d={ICELAND_PATH}
          fill="#E8F5EE"
          stroke="#21b766"
          strokeWidth="2.5"
          strokeLinejoin="round"
          filter="url(#softShadow)"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
        />

        {/* Tiny "Reykjavík" star marker for orientation */}
        <g transform="translate(170, 370)" opacity="0.9">
          <circle r="3.2" fill="#121212" />
          <text
            x={-8}
            y={-6}
            fontSize="10"
            fontWeight="700"
            fill="#121212"
            fontFamily="Outfit, sans-serif"
            textAnchor="end"
          >
            Reykjavík
          </text>
        </g>

        {/* Landscape details inside Iceland */}
        <g opacity="0.5">
          <g transform="translate(420, 290)">
            <path d="M-22 18 L-8 -10 L4 8 L14 -6 L24 18 Z" fill="#C8E6D2" stroke="#16803d" strokeWidth="1.5" strokeLinejoin="round" />
          </g>
          <g transform="translate(620, 320)">
            <path d="M-18 14 L0 -14 L18 14 Z" fill="#C8E6D2" stroke="#16803d" strokeWidth="1.5" strokeLinejoin="round" />
          </g>
          <g transform="translate(500, 380)">
            <path d="M-16 14 L0 -12 L16 14 Z" fill="#F15D4E" fillOpacity="0.35" stroke="#F15D4E" strokeWidth="1.5" strokeLinejoin="round" />
            <path d="M-5 -4 L0 -14 L5 -4" stroke="#F15D4E" strokeWidth="1.4" fill="none" />
          </g>
          <g transform="translate(700, 370)" stroke="#3B82F6" strokeWidth="1.6" fill="none" strokeLinecap="round">
            <path d="M-6 -10 L-6 8 M0 -10 L0 10 M6 -10 L6 8" />
            <path d="M-10 10 Q0 16 10 10" />
          </g>
        </g>

        {/* Hidden measurement path — used only for getPointAtLength */}
        <path
          ref={measurePathRef}
          d={routePath}
          fill="none"
          stroke="none"
        />

        {/* Visible dotted training path (the road ahead) */}
        <motion.path
          d={routePath}
          fill="none"
          stroke="#121212"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeDasharray="2 10"
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.75 }}
          transition={{ duration: 1.2, ease: "easeInOut" }}
          data-testid="path-dotted"
        />

        {/* Completed portion of the path — solid brand green up to the user's current position */}
        {pathLength > 0 && completedLength > 0 && (
          <motion.path
            d={routePath}
            fill="none"
            stroke="#21b766"
            strokeWidth="4.5"
            strokeLinecap="round"
            strokeDasharray={`${completedLength} ${pathLength + 10}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            data-testid="path-completed"
          />
        )}        {/* Belt nodes */}
        {BELTS.map((belt, idx) => {
          const isActive = belt.id === activeId;
          return (
            <g
              key={belt.id}
              transform={`translate(${belt.map.x}, ${belt.map.y})`}
              style={{ cursor: "pointer" }}
              onClick={() => onSelect(belt.id)}
              data-testid={`map-node-${belt.slug}`}
            >
              {/* Invisible hit target so the whole label + node area is clickable */}
              <rect x={-36} y={-58} width={72} height={90} rx={12} fill="transparent" />

              <motion.g
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 + idx * 0.06, duration: 0.4 }}
              >
                <rect x={-36} y={-58} width={72} height={20} rx={10} fill="#121212" />
                <text
                  x={0}
                  y={-44}
                  textAnchor="middle"
                  fill="#fff"
                  fontSize="11"
                  fontWeight="700"
                  fontFamily="Outfit, sans-serif"
                  letterSpacing="0.5"
                >
                  {belt.locked ? "LEVEL 4" : `W ${belt.weekStart}–${belt.weekEnd}`}
                </text>
              </motion.g>

              {isActive && (
                <motion.circle
                  r={28}
                  fill="none"
                  stroke={belt.color === "#FFFFFF" ? "#21b766" : belt.color}
                  strokeWidth="3"
                  initial={{ scale: 0.6, opacity: 0 }}
                  animate={{ scale: 1, opacity: 0.6 }}
                  transition={{ duration: 0.4 }}
                />
              )}

              <motion.circle
                r={isActive ? 18 : 15}
                fill={belt.color}
                stroke={belt.ring}
                strokeWidth="3"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.5 + idx * 0.06, type: "spring", stiffness: 220, damping: 16 }}
                whileHover={{ scale: isActive ? 1.05 : 1.15 }}
              />

              {belt.locked && (
                <foreignObject x={-7} y={-7} width={14} height={14}>
                  <div style={{ color: "#fff", display: "flex" }}>
                    <Lock size={14} strokeWidth={2.5} />
                  </div>
                </foreignObject>
              )}
            </g>
          );
        })}

        {/* Sensei (Sandra) — waiting at the next belt on the path */}
        {senseiPoint && (
          <g
            transform={`translate(${senseiPoint.x}, ${senseiPoint.y})`}
            data-testid="sensei-marker"
            style={{ cursor: "pointer" }}
            onClick={handleSenseiClick}
          >
            <motion.g
              initial={{ opacity: 0, scale: 0.6 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.4, duration: 0.5 }}
            >
              <circle r="24" fill="#F15D4E" fillOpacity="0.18" pointerEvents="none" />
              <image
                href={SENSEI_IMG}
                x={-17}
                y={-17}
                width={34}
                height={34}
                clipPath="url(#senseiClip)"
                preserveAspectRatio="xMidYMid slice"
              />
              <circle r="17" fill="none" stroke="#fff" strokeWidth="2.5" />
              <circle r="17" fill="none" stroke="#F15D4E" strokeWidth="1.5" />
              <g transform="translate(0, 32)">
                <rect x={-32} y={-9} width={64} height={18} rx={9} fill="#F15D4E" />
                <text
                  x={0}
                  y={4}
                  textAnchor="middle"
                  fill="#fff"
                  fontSize="10"
                  fontWeight="800"
                  fontFamily="Outfit, sans-serif"
                  letterSpacing="0.5"
                >
                  SANDRA
                </text>
              </g>
            </motion.g>

            {/* Speech bubble — appears when Sandra is tapped */}
            <AnimatePresence>
              {senseiMsg && (
                <motion.g
                  key={senseiMsg.key}
                  initial={{ opacity: 0, y: -4, scale: 0.9 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -4, scale: 0.95 }}
                  transition={{ duration: 0.25, ease: [0.2, 0.7, 0.2, 1] }}
                  style={{ pointerEvents: "none" }}
                  data-testid="sensei-speech"
                >
                  <g transform="translate(0, -46)">
                    <rect x={-88} y={-16} width={176} height={30} rx={15} fill="#121212" />
                    <path d="M -6 14 L 0 22 L 6 14 Z" fill="#121212" />
                    <text
                      x={0}
                      y={4}
                      textAnchor="middle"
                      fill="#fff"
                      fontSize="12"
                      fontWeight="700"
                      fontFamily="Outfit, sans-serif"
                    >
                      {senseiMsg.text}
                    </text>
                  </g>
                </motion.g>
              )}
            </AnimatePresence>
          </g>
        )}

        {/* User avatar — "You are here" */}
        {userPoint && (
          <g transform={`translate(${userPoint.x}, ${userPoint.y})`} data-testid="user-marker" style={{ pointerEvents: "none" }}>
            <motion.g
              initial={{ opacity: 0, scale: 0.6 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, duration: 0.6 }}
            >
              <motion.circle
                r="26"
                fill="#21b766"
                fillOpacity="0.22"
                animate={{ scale: [1, 1.4, 1], opacity: [0.6, 0, 0.6] }}
                transition={{ duration: 2.2, repeat: Infinity, ease: "easeOut" }}
              />
              <circle r="19" fill="#21b766" stroke="#fff" strokeWidth="3" />
              <g fill="#fff" stroke="none">
                <circle cx="0" cy="-4" r="4" />
                <path d="M-8 11 Q 0 1 8 11 L 8 12 L -8 12 Z" />
              </g>
              <g transform="translate(0, -34)">
                <rect x={-50} y={-10} width={100} height={20} rx={10} fill="#121212" />
                <text
                  x={0}
                  y={4}
                  textAnchor="middle"
                  fill="#fff"
                  fontSize="10"
                  fontWeight="800"
                  fontFamily="Outfit, sans-serif"
                  letterSpacing="0.5"
                >
                  {`YOU · WEEK ${currentWeek}`}
                </text>
              </g>
            </motion.g>
          </g>
        )}

        {/* Level-complete ribbons — rendered LAST so they sit above the user/sensei markers */}
        {BELTS.filter((b) => !b.locked && b.levelComplete).map((belt) => (
          <g
            key={`lvl-${belt.id}`}
            transform={`translate(${belt.map.x}, ${belt.map.y + 34})`}
            data-testid={`level-complete-${belt.slug}`}
            style={{ pointerEvents: "none" }}
          >
            <rect x={-62} y={-10} width={124} height={20} rx={10} fill="#F15D4E" />
            <text
              x={0}
              y={3.5}
              textAnchor="middle"
              fill="#fff"
              fontSize="10"
              fontWeight="700"
              fontFamily="Outfit, sans-serif"
              letterSpacing="0.7"
            >
              {`LEVEL ${belt.levelComplete} COMPLETE`}
            </text>
          </g>
        ))}
      </svg>

      {/* Map chrome */}
      <div className="absolute top-4 right-4 flex items-center gap-2 rounded-full bg-white/90 backdrop-blur px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#52525B] shadow-sm border border-zinc-200/60">
        <span className="h-1.5 w-1.5 rounded-full bg-[#21b766]" />
        Ísland · Training Map
      </div>
      <div className="absolute top-4 left-4 flex flex-col items-center justify-center gap-0 rounded-md bg-[#121212] text-white px-2 py-1.5 text-[10px] font-bold uppercase tracking-[0.18em]">
        <span>N</span>
        <span className="opacity-60 leading-none">↑</span>
      </div>

      {/* Legend */}
      <div className="absolute bottom-3 left-3 flex items-center gap-3 rounded-full bg-white/95 backdrop-blur px-3 py-1.5 text-[10px] font-semibold text-[#121212] shadow-sm border border-zinc-200/60">
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full bg-[#21b766]" /> You
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full bg-[#F15D4E]" /> Sandra
        </span>
        <span className="inline-flex items-center gap-1.5 text-[#52525B]">
          <span className="h-1 w-6 rounded-full bg-[#21b766]" /> Trained
        </span>
        <span className="inline-flex items-center gap-1.5 text-[#52525B]">
          <span className="h-1 w-6 rounded-full bg-[#121212]" style={{ backgroundImage: 'radial-gradient(circle at center, #121212 40%, transparent 40%)', backgroundSize: '6px 6px', backgroundColor: 'transparent' }} /> Ahead
        </span>
      </div>
    </div>
  );
};
